import { TrackingFrame } from "mannequin/src/types/tracking/mannequin";
import { RingBuffer } from "ring-buffer-ts";
import {
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  Vector2,
  Vector3,
} from "three";
import { PositionDetails } from "../../lib/misc/position";

import {
  PlayEventType,
  playEventTypeMap,
  TrackedEvent,
} from "../../lib/types/statsApi";
import { Context } from "../context";
import { Frames } from "./frames";

class ActorHistoryStack {
  candidate?: number;
  private _last?: number;
  private _current?: number;

  update(current?: number) {
    this._last = this._current;
    this._current = current;
  }

  get last(): number | undefined {
    return this._last;
  }

  get current(): number | undefined {
    return this._current;
  }
}

type PlayPhase = "warmup" | "pitch" | "hit" | "field" | "inhand" | "flight";

function playPhaseInAir(phase: PlayPhase): boolean {
  return ["pitch", "hit", "flight"].includes(phase);
}

export class TrackedEventsGenerator {
  private contextRef: Context;

  // TODO: use actors set here and run posing infront of render time?
  //       i.e. pose 5s ahead of `now` and cache the transformations
  private actorCentroids: Map<number, PositionDetails> = new Map();

  private ballAge?: number;
  private lastBallPos: Vector3 = new Vector3();
  private ballPos: Vector3 = new Vector3();
  private ballDeltaP: Vector3 = new Vector3();
  private ballDeltaV?: Vector3 = new Vector3();

  private newBallTraj = true;

  private deBouncers = {
    ballBouncing: false,
    ballRolling: false,
    hitting: false,
    catchingOut: false,
    releasing: false,
  };

  private touchCnt = 0;
  private withCnt = 0;

  private touchDistSq = 6 ** 2;
  private actorsTouchedBall = new ActorHistoryStack();
  private actorWithBall = new ActorHistoryStack();

  private state = {
    playPhase: "warmup" as PlayPhase,
    inPlay: false,
  };

  // TODO: we assume 30 fps tracking
  private endOfPlayDelay = 3 * 30; // frames
  private pendingEopCnt: number | undefined = undefined;

  private ballDir2D = new Vector3();

  private vipPosIds = { batter: 10, pitcher: 1 };

  private actorPos2D = { batter: new Vector2(), pitcher: new Vector2() };
  private landmarks = { mound: new Vector2(0, -60), plate: new Vector2(0, 0) };

  private pitcherAtMound = false;
  private batterAtPlate = false;

  private moundToPlate = new Vector3(0, 0, -1);
  private moundTo1stBase = new Vector3(0, 1, 0);
  private moundTo2ndBase = new Vector3(0, 0, 1);
  private moundTo3rdBase = new Vector3(0, -1, 0);
  private pitchMaxAngle = (15 * Math.PI) / 180;

  private toProcess = {
    ballPos: false,
    touchedBall: false,
    withBall: false,
    actorPositions: false,
  };

  private prevFrameNum = -1;

  private prevFrame?: TrackingFrame;

  hasBallIndicator: Mesh;

  constructor(context: Context) {
    this.contextRef = context;

    const material = new MeshBasicMaterial({ color: 0xff0000 });
    const geometry = new SphereGeometry(1, 16, 8);
    this.hasBallIndicator = new Mesh(geometry, material);
    this.hasBallIndicator.position.set(0, 4, 0);
    this.hasBallIndicator.visible = false;
  }

  writeEvents(frames: Frames, removeExisting = true) {
    const current = frames.current;
    if (!current || this.prevFrameNum == current.num) {
      return;
    }

    for (const frame of [...frames.missed, frames.current]) {
      // TODO: error here?
      if (!frame) continue;

      this.updateCentroids(frame);
      this.updateBall(frame);

      if (this.ballAge == 0) {
        this.updateActorsTouchedBall();
      }

      this.updatePlayerInfo();

      if (
        removeExisting &&
        frame.trackedEvents != undefined &&
        frame.trackedEvents.length > 0
      ) {
        if (this.contextRef.state.warnOnRemovePlayEvents) {
          console.warn(
            `removing pre-existing tracked event data for frame at ${
              frame.time
            }: ${frame.trackedEvents.map((e) => e.eventType)}`
          );
        }

        frame.trackedEvents.length = 0;
      }

      this.fireEvents(frame);

      this.prevFrame = frame;
    }

    this.prevFrameNum = current.num;
  }

  private updateCentroids(frame: TrackingFrame) {
    for (const pose of frame.actorPoses) {
      let entry = this.actorCentroids.get(pose.uid);
      if (entry == undefined) {
        entry = new PositionDetails();
        this.actorCentroids.set(pose.uid, entry);

        // // NB: useful for debugging catching etc
        // const material = new MeshBasicMaterial({ color: 0xff0000 });
        // const geometry = new SphereGeometry(1, 16, 8);
        // const mesh = new Mesh(geometry, material);
        // entry.centroid.add(mesh);

        this.contextRef.scene.add(entry.centroid);
      }

      // TODONOW: fix position details
      // const points = this.jointMap.asJointPositions(pos);
      // entry.update(points);
    }
  }

  private updateBall(frame: TrackingFrame) {
    const ballPosition = frame.ballPosition;

    if (ballPosition) {
      this.ballPos.set(ballPosition[0], ballPosition[1], ballPosition[2]);

      if (this.prevFrame && this.prevFrame.ballPosition) {
        this.newBallTraj = false;

        const lastBallPosition = this.prevFrame.ballPosition;
        this.lastBallPos.set(
          lastBallPosition[0],
          lastBallPosition[1],
          lastBallPosition[2]
        );
        if (!this.ballDeltaV) {
          this.ballDeltaV = new Vector3();
        }
        this.ballDeltaV.copy(this.ballDeltaP).negate();
        this.ballDeltaP.subVectors(this.lastBallPos, this.ballPos);
        this.ballDeltaV.add(this.ballDeltaP);

        // console.log("this.ballDeltaV:", this.ballDeltaV.toArray());

        this.ballDir2D.set(this.ballDeltaP.x, 0, this.ballDeltaP.z).normalize();
      } else {
        this.newBallTraj = true;
      }
      this.ballAge = 0;

      this.toProcess.ballPos = true;
    } else {
      if (this.ballAge != undefined) this.ballAge++;
    }
  }

  private updateActorsTouchedBall() {
    let closestDistSq = Infinity;
    let closestActor: number | undefined = undefined;
    let closestPosition: Vector3 | undefined = undefined;

    // console.log("updating actorsTouchedBall");

    for (const [posId, details] of this.actorCentroids) {
      const distSq = details.centroid.position.distanceToSquared(this.ballPos);
      if (distSq < closestDistSq && posId != this.vipPosIds.batter) {
        closestDistSq = distSq;
        closestActor = posId;
        closestPosition = details.centroid.position;
      }
    }

    if (closestDistSq < this.touchDistSq) {
      this.touchCnt++;
      if (closestActor != this.actorsTouchedBall.candidate) {
        this.actorsTouchedBall.candidate = closestActor;

        if (this.actorsTouchedBall.current == undefined || this.touchCnt > 1) {
          this.actorsTouchedBall.update(closestActor);
          this.touchCnt = 0;
          this.toProcess.touchedBall = true;
        }
      }

      const ballToHand =
        this.lastBallPos &&
        closestPosition &&
        closestPosition.distanceToSquared(this.lastBallPos) > closestDistSq;
      // ball is going towards player, so let's assume they're holding it
      // from now on
      this.withCnt++;
      if (closestActor != this.actorWithBall.candidate) {
        this.actorWithBall.candidate = closestActor;

        if (this.actorWithBall.current == undefined || this.withCnt > 1) {
          this.actorWithBall.update(ballToHand ? closestActor : undefined);
          this.toProcess.withBall = true;
        }
      }
    } else {
      this.actorWithBall.update(undefined);
      this.toProcess.withBall = true;
    }

    if (this.toProcess.withBall) {
      const posId = this.actorWithBall.current;

      if (this.contextRef.state.showBallOwner) {
        if (posId != undefined) {
          const centroid = this.actorCentroids.get(posId)?.centroid;
          if (centroid) {
            centroid.add(this.hasBallIndicator);
            this.hasBallIndicator.visible = true;
          }
        } else {
          this.hasBallIndicator.visible = false;
        }
      }

      // // NB: previous method of debugging ball ownership left here in case
      // this.contextRef.state.positionId =
      //   this.actorWithBall.current != undefined
      //     ? this.actorWithBall.current
      //     : 17;
    }

    return closestActor;
  }

  private updatePlayerInfo() {
    const pitcherDetails = this.actorCentroids.get(this.vipPosIds.pitcher);
    const batterDetails = this.actorCentroids.get(this.vipPosIds.batter);

    const pitcherZoneRadiusSq = 15 ** 2;
    const batterZoneRadiusSq = 6 ** 2;

    const pitcherWasAtMound = this.pitcherAtMound;
    const batterWasAtPlate = this.batterAtPlate;

    const pitcherPos = pitcherDetails?.centroid.position;
    if (pitcherPos) {
      this.actorPos2D.pitcher.setX(pitcherPos.x).setY(pitcherPos.z);

      this.pitcherAtMound =
        this.actorPos2D.pitcher.distanceToSquared(this.landmarks.mound) <
        pitcherZoneRadiusSq;
    }

    const batterPos = batterDetails?.centroid.position;
    if (batterPos) {
      this.actorPos2D.batter.setX(batterPos.x).setY(batterPos.z);

      this.batterAtPlate =
        this.actorPos2D.batter.distanceToSquared(this.landmarks.plate) <
        batterZoneRadiusSq;
    }

    if (
      this.pitcherAtMound != pitcherWasAtMound ||
      this.batterAtPlate != batterWasAtPlate
    ) {
      this.toProcess.actorPositions = true;
      // console.warn(`PLAYERS INFO`);
      // if (this.pitcherAtMound != pitcherWasAtMound) {
      //   console.warn(`    pitcher ${this.pitcherAtMound ? "in" : "out"}`);
      //   console.warn(`        mdist ${this.actorPos2D.pitcher.manhattanDistanceTo(this.landmarks.mound)}`);
      //   console.warn(`        dist ${this.actorPos2D.pitcher.distanceTo(this.landmarks.mound)}`);
      // }
      // if (this.batterAtPlate != batterWasAtPlate) {
      //   console.warn(`    batter ${this.batterAtPlate ? "in" : "out"}`);
      //   console.warn(`        mdist ${this.actorPos2D.batter.manhattanDistanceTo(this.landmarks.plate)}`);
      //   console.warn(`        dist ${this.actorPos2D.batter.distanceTo(this.landmarks.plate)}`);
      // }
    }
  }

  private fireEvents(frame: TrackingFrame) {
    if (this.pendingEopCnt != undefined) {
      if (this.pendingEopCnt-- <= 0) {
        this.publishTrackedEvent(frame, PlayEventType.END_OF_PLAY);
        this.pendingEopCnt = undefined;
        this.state.playPhase = "warmup";
        this.state.inPlay = false;
      }
      // console.warn(`this.pendingEopCnt: ${this.pendingEopCnt}`);
    }

    if (this.toProcess.actorPositions) {
      if (this.batterAtPlate && this.pitcherAtMound) {
        this.updateInPlay(frame, true);
      } else if (this.state.inPlay && !this.pitcherAtMound) {
        this.updateInPlay(frame, false);
      }

      this.toProcess.actorPositions = false;
    }

    this.deBouncers.releasing = false;

    if (this.toProcess.withBall) {
      if (this.actorWithBall.current == undefined) {
        // ball thrown
        if (this.actorWithBall.last == 1) {
          if (this.state.playPhase == "warmup") {
            // TODO: use more efficient 2D angle calc here
            if (
              this.ballDir2D.angleTo(this.moundToPlate) < this.pitchMaxAngle
            ) {
              this.state.playPhase = "pitch";
              this.publishTrackedEvent(
                frame,
                PlayEventType.BALL_WAS_PITCHED,
                this.vipPosIds.pitcher
              );
            } else if (
              this.ballDir2D.angleTo(this.moundTo1stBase) <
                this.pitchMaxAngle ||
              this.ballDir2D.angleTo(this.moundTo2ndBase) <
                this.pitchMaxAngle ||
              this.ballDir2D.angleTo(this.moundTo3rdBase) < this.pitchMaxAngle
            ) {
              this.state.playPhase = "flight";
              this.publishTrackedEvent(
                frame,
                PlayEventType.PICK_OFF_BALL_RELEASED,
                this.vipPosIds.pitcher
              );
            }
          }
        } else if (this.actorWithBall.last != undefined) {
          const wasReleasing = this.deBouncers.releasing;
          this.deBouncers.releasing = true;

          if (!wasReleasing) {
            this.state.playPhase = "flight";
            this.publishTrackedEvent(
              frame,
              PlayEventType.BALL_WAS_RELEASED,
              this.actorWithBall.last
            );
          }
        }
      } else if (
        this.state.playPhase != "warmup" &&
        this.actorWithBall.last == undefined
      ) {
        if (playPhaseInAir(this.state.playPhase)) {
          // detect catch out
          const wasCatchingOut = this.deBouncers.catchingOut;
          this.deBouncers.catchingOut = this.state.playPhase == "hit";

          if (!wasCatchingOut && this.deBouncers.catchingOut) {
            this.publishTrackedEvent(
              frame,
              PlayEventType.BALL_WAS_CAUGHT_OUT,
              this.actorWithBall.current
            );
            this.updateInPlay(frame, false);
          } else {
            this.publishTrackedEvent(
              frame,
              PlayEventType.BALL_WAS_CAUGHT,
              this.actorWithBall.current
            );
          }
        } else if (this.state.playPhase == "field") {
          this.publishTrackedEvent(
            frame,
            PlayEventType.BALL_WAS_FIELDED,
            this.actorWithBall.current
          );
        }

        this.state.playPhase = "inhand";
      }

      this.toProcess.withBall = false;
    }

    if (this.toProcess.ballPos) {
      // detect bounce
      const wasBouncing = this.deBouncers.ballBouncing;

      this.deBouncers.ballBouncing =
        this.ballPos.y < 1 &&
        this.ballDeltaV != undefined &&
        this.ballDeltaV.y < -0.05;

      if (!wasBouncing && this.deBouncers.ballBouncing) {
        this.state.playPhase = "flight";
        this.publishTrackedEvent(frame, PlayEventType.BALL_BOUNCE);
      }

      // detect roll
      const wasRolling = this.deBouncers.ballRolling;

      this.deBouncers.ballRolling =
        this.ballPos.y < 1 &&
        this.ballDeltaV != undefined &&
        this.ballDeltaV.y < 0;

      if (
        !this.deBouncers.ballBouncing &&
        !wasRolling &&
        this.deBouncers.ballRolling
      ) {
        this.state.playPhase = "field";
      }

      // detect hit
      const batterDetails = this.actorCentroids.get(this.vipPosIds.batter);
      if (batterDetails) {
        const wasHitting = this.deBouncers.hitting;
        const batterDistSq = batterDetails.centroid.position.distanceToSquared(
          this.ballPos
        );

        this.deBouncers.hitting =
          !this.newBallTraj &&
          batterDistSq < this.touchDistSq &&
          this.ballDeltaV != undefined &&
          this.ballDeltaV.z > 2;

        if (
          !wasHitting &&
          this.deBouncers.hitting &&
          this.state.playPhase == "pitch"
        ) {
          this.state.playPhase = "hit";
          this.publishTrackedEvent(
            frame,
            PlayEventType.BALL_WAS_HIT,
            this.vipPosIds.batter
          );
        }
      }

      this.toProcess.ballPos = false;
    }
  }

  private updateInPlay(frame: TrackingFrame, inPlay: boolean) {
    if (inPlay) {
      if (!this.state.inPlay) {
        this.publishTrackedEvent(frame, PlayEventType.BEGIN_OF_PLAY);
        this.state.playPhase = "warmup";
        this.state.inPlay = true;
      }
      this.pendingEopCnt = undefined;
    } else {
      this.pendingEopCnt = this.endOfPlayDelay;
    }
  }

  private publishTrackedEvent(
    frame: TrackingFrame,
    type: PlayEventType,
    position?: number
  ) {
    if (!this.state.inPlay && type != PlayEventType.BEGIN_OF_PLAY) {
      // don't publish intraplay events
      return;
    }

    // TODO: fill in dummy values or not?
    const event = {} as TrackedEvent;

    if (position) event.position = position;
    // TODO: verify that timestamps are equivalent
    event.timestamp = frame.timestamp;
    event.eventTypeId = type;
    event.eventType = playEventTypeMap[type];

    if (frame.trackedEvents == undefined) {
      frame.trackedEvents = [];
    }

    // restart counter if we see an in play event
    const inPlayEvent = [
      PlayEventType.BALL_WAS_PITCHED,
      PlayEventType.BALL_WAS_HIT,
    ].includes(type);
    if (inPlayEvent && this.pendingEopCnt != undefined)
      this.pendingEopCnt = this.endOfPlayDelay;

    // TODO: remove debug prints
    const indent = ![
      PlayEventType.BEGIN_OF_PLAY,
      PlayEventType.END_OF_PLAY,
    ].includes(type);
    console.error(
      `${indent ? "    " : ""}${event.eventType}${
        position != undefined ? ` (${position})` : ""
      }`
    );

    frame.trackedEvents.push(event);

    return true;
  }
}

export class EventList {
  private stack: RingBuffer<TrackedEvent> = new RingBuffer(50);
  private prevFrameNum: number | undefined = undefined;

  update(frames: Frames) {
    if (!frames.current || this.prevFrameNum == frames.current.num) {
      return false;
    }

    for (const frame of [...frames.missed, frames.current]) {
      if (frame.trackedEvents == undefined || frame.trackedEvents.length == 0) {
        continue;
      }

      // TODO: add back under debug flag - useful for debugging stack
      // console.warn(
      //   `m: ${frames.missed.length} fn: ${frames.current.num} adding ${frame.trackedEvents.length} events to stack: ` +
      //     `${frame.trackedEvents.map((event) => ` ${event.eventType}`)}`
      // );

      this.stack.add(...frame.trackedEvents);
    }

    this.prevFrameNum = frames.current.num;
  }

  lookAhead(
    currentEvent: TrackedEvent,
    query: PlayEventType[],
    onFound?: (found: TrackedEvent) => void,
    onNotFound?: () => void,
    maxLookAhead = 8
  ) {
    // TODO: improve efficiency of this
    const stackStart = this.indexOf(currentEvent);
    const stackEnd = this.length();
    if (stackStart == undefined) {
      console.error("current event not found on stack");
      return false;
    }

    for (
      let i = stackStart + 1;
      i < stackEnd && i - stackStart < maxLookAhead;
      i++
    ) {
      const fwdEvent = this.get(i);
      if (!fwdEvent) {
        console.error("missing fwd event");
      } else if (query.includes(fwdEvent.eventTypeId)) {
        if (onFound) {
          onFound(fwdEvent);
        }
        return;
      }
    }

    if (onNotFound) {
      onNotFound();
    }
  }

  indexOf(eventToFind: TrackedEvent): number | undefined {
    // TODO: think of better datastructure to improve efficiency
    const length = this.length();
    for (let i = 0; i < length; i++) {
      const event = this.stack.get(i);
      if (event == eventToFind) {
        return i;
      }
    }

    return undefined;
  }

  length(): number {
    return this.stack.getBufferLength();
  }

  get(index: number): TrackedEvent | undefined {
    return this.stack.get(index);
  }
}
